import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  productsApi,
  sellersApi,
  schedulesApi,
  type Product,
  type ProductsListParams,
  type SellerListItem,
  type Schedule,
} from '../lib/api';
import { useAuth } from '../lib/auth';

const PAGE_SIZE = 50;

export default function ProductsList() {
  const { user } = useAuth();

  const [filterSeller, setFilterSeller]     = useState<number | ''>('');
  const [filterSchedule, setFilterSchedule] = useState<number | ''>('');
  const [filterActive, setFilterActive]     = useState<'' | 'on' | 'off'>('');
  const [q, setQ]                           = useState('');
  const [offset, setOffset]                 = useState(0);

  const [sellers, setSellers]     = useState<SellerListItem[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  const [items, setItems] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      sellersApi.list().then((r) => setSellers(r.sellers)).catch(() => { /* noop */ });
    }
  }, [user]);

  useEffect(() => {
    schedulesApi.list(typeof filterSeller === 'number' ? filterSeller : undefined)
      .then((r) => setSchedules(r.schedules))
      .catch(() => setSchedules([]));
  }, [filterSeller]);

  useEffect(() => {
    setLoading(true);
    const params: ProductsListParams = { limit: PAGE_SIZE, offset };
    if (filterSeller !== '')   params.seller_id = filterSeller;
    if (filterSchedule !== '') params.schedule_group_id = filterSchedule;
    if (filterActive === 'on') params.is_active = true;
    if (filterActive === 'off') params.is_active = false;
    if (q.trim())              params.q = q.trim();
    productsApi.list(params)
      .then((r) => { setItems(r.products); setTotal(r.total); setErr(null); })
      .catch((e) => setErr(String(e?.message ?? e)))
      .finally(() => setLoading(false));
  }, [filterSeller, filterSchedule, filterActive, q, offset]);

  function sellerLabel(id: number) {
    const s = sellers.find((x) => x.id === id);
    return s ? (s.display_name || s.login) : `#${id}`;
  }

  function scheduleLabel(id: number | null) {
    if (id == null) return <span className="muted">—</span>;
    const s = schedules.find((x) => x.id === id);
    return s ? s.name : `#${id}`;
  }

  return (
    <div className="container">
      <div className="page-head">
        <h2>Товары</h2>
        <Link to="/products/new" className="btn primary">+ Новый товар</Link>
      </div>

      <div className="row" style={{ marginBottom: 14, gap: 16, alignItems: 'flex-end' }}>
        {user?.role === 'admin' && (
          <label>
            <div className="muted" style={{ fontSize: 12 }}>Продавец</div>
            <select
              value={filterSeller}
              onChange={(e) => { setOffset(0); setFilterSeller(e.target.value ? Number(e.target.value) : ''); }}
            >
              <option value="">Все</option>
              {sellers.map((s) => (
                <option key={s.id} value={s.id}>{s.display_name || s.login}</option>
              ))}
            </select>
          </label>
        )}
        <label>
          <div className="muted" style={{ fontSize: 12 }}>Расписание</div>
          <select
            value={filterSchedule}
            onChange={(e) => { setOffset(0); setFilterSchedule(e.target.value ? Number(e.target.value) : ''); }}
          >
            <option value="">Все</option>
            {schedules.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
        <label>
          <div className="muted" style={{ fontSize: 12 }}>Активность</div>
          <select
            value={filterActive}
            onChange={(e) => { setOffset(0); setFilterActive(e.target.value as '' | 'on' | 'off'); }}
          >
            <option value="">Все</option>
            <option value="on">Активные</option>
            <option value="off">Скрытые</option>
          </select>
        </label>
        <label style={{ flex: 1, minWidth: 200 }}>
          <div className="muted" style={{ fontSize: 12 }}>Поиск (имя / артикул)</div>
          <input
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            placeholder="например: Абигейл / Fla00035c"
          />
        </label>
      </div>

      {err && <div className="error">{err}</div>}
      {loading && <p className="muted">Загрузка…</p>}

      {!loading && items.length === 0 ? (
        <p className="muted">Ничего не найдено.</p>
      ) : (
        <>
          <table className="grid">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Фото</th>
                <th>Артикул</th>
                <th>Название</th>
                {user?.role === 'admin' && <th>Продавец</th>}
                <th>Цена ₽</th>
                <th>Расписание</th>
                <th>Окно</th>
                <th>Активен</th>
                <th>Последний push</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.images[0]
                      ? <img src={p.images[0]} alt="" className="thumb" />
                      : <span className="thumb empty" />}
                  </td>
                  <td><code>{p.external_offer_id}</code></td>
                  <td><Link to={`/products/${p.id}`}>{p.name}</Link></td>
                  {user?.role === 'admin' && <td>{sellerLabel(p.seller_id)}</td>}
                  <td>{p.price_rub.toLocaleString('ru-RU')}</td>
                  <td>{scheduleLabel(p.schedule_group_id)}</td>
                  <td>
                    <span className={`badge ${p.current_window_state === 'open' ? 'ok' : 'warn'}`}>
                      {p.current_window_state}
                    </span>
                  </td>
                  <td>{p.is_active ? '✓' : '—'}</td>
                  <td>
                    {p.last_pushed_at ? (
                      <span title={p.last_push_status ?? ''}>
                        {p.last_push_status === 'ok' ? '✓' : '!'} {p.last_pushed_at.slice(0, 16)}
                      </span>
                    ) : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
            <span className="muted">
              {offset + 1}–{Math.min(offset + items.length, total)} из {total}
            </span>
            <div className="row">
              <button className="btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>← Назад</button>
              <button className="btn" disabled={offset + items.length >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Вперёд →</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
